import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { getModelProcess } from '../services/model-process';
import { urlParam } from '../utils/url-param';
import {
  ErrorResponse,
  ModelResponse,
  ModelResponseSuccess,
} from '../types/common-types';
import { CarSliceType } from '../types/car-slice-type';

const uId = urlParam('uid');
const mId = urlParam('mid');
const baseUrl = process.env.REACT_APP_BASE_URL;
const carModelUrl = `${baseUrl}/model-retrieve/${uId}/${mId}`;

export const fetchCarModel = createAsyncThunk('car/fetchCarModel', async () => {
  let data;
  try {
    const response = await fetch(carModelUrl);

    data = await response.json();

    return await getModelProcess(data);
  } catch (error) {
    console.log('@getModel error');
    return data;
  }
});

const initialState: CarSliceType = {
  userId: undefined,
  modelId: undefined,
  carStatus: 'VDP',
  activeTab: 'exterior',
  prevTab: null,
  interiorStatus: null,
  showVideo: false,
  activeVideo: null,
  loading: false,
  error: '',
  isFullscreen: false,
  engineVideoFilename: null,
  engineVideoUrl: null,
  pushStartVideoUrl: null,
  carImageRatio: 0,
  // exterior
  carHotSpotCardOpen: false,
  carHotSpotCardStatus: 1,
  carHotSpotEnable: true,
  centerConsoleUrl: null,
  backSeatUrl: null,
  carHotSpotPositions: [],
  autoHotspots: [],
  carModel: [],
  closeHotSpotPosition: { x: 0, y: 0 },
  currentCarHotSpotIndex: '0',
  exterior360Image: [],
  fetchCarModelStatus: 'idle',
  car360Loaded: false,
  dragToExploreVisibility: true,
  rotate360Visibility: false,
  exteriorZoomScale: 1,
  isZoomEnabled: false,
  purchaseDialog: false,
  galleryTab: 'gallery-exterior',
  videoTourExists: false,
};

const carSlice = createSlice({
  initialState,
  name: 'car',
  reducers: {
    activeTabSet(state, action) {
      state.prevTab = state.activeTab;
      state.activeTab = action.payload;
      state.isZoomEnabled = false;
    },
    zoomEnabledSet: (state, { payload }) => {
      state.isZoomEnabled = payload;
    },
    galleryTabSet: (state, { payload }) => {
      state.galleryTab = payload;
    },
    purchaseDialogToggled: (state) => {
      state.purchaseDialog = !state.purchaseDialog;
    },
    activeVideoSet(state, { payload }) {
      state.activeVideo = payload;
    },
    engineVideoUrlSet(state, { payload }) {
      state.engineVideoUrl = payload;
    },
    pushStartVideoUrlSet(state, { payload }) {
      state.pushStartVideoUrl = payload;
    },
    carHotSpotCardOpenToggled: (state) => {
      state.carHotSpotCardOpen = !state.carHotSpotCardOpen;
    },
    carStatusSet: (state, action) => {
      state.carStatus = action.payload;
    },
    isFullScreenToggled: (state, { payload }) => {
      state.isFullscreen = payload;
    },
    interiorStatusSet: (state, { payload }) => {
      state.interiorStatus = payload;
    },
    showVideoToggled: (state) => {
      state.showVideo = !state.showVideo;
    },
    carHotSpotEnableSet: (state, { payload }) => {
      state.carHotSpotEnable = payload;
    },
    carHotSpotPositionsSet: (state, { payload }) => {
      state.carHotSpotPositions = [];

      state.carHotSpotPositions = payload;
    },
    carImageRatioSet: (state, { payload }) => {
      state.carImageRatio = payload;
    },
    car360LoadedSet: (state, action) => {
      state.car360Loaded = action.payload;
    },
    dragToExploreVisibilitySet: (state, action) => {
      state.dragToExploreVisibility = action.payload;
    },
    rotate360VisibilitySet: (state, action) => {
      state.rotate360Visibility = action.payload;
    },
    exteriorZoomScaleSet: (state, action) => {
      state.exteriorZoomScale = action.payload;
    },
    closeHotSpotPositionSet: (state, { payload }) => {
      const { x, y } = payload;
      state.closeHotSpotPosition.x = x;
      state.closeHotSpotPosition.y = y;
    },
    videoTourExistsSet: (state, action) => {
      state.videoTourExists = action.payload;
    },
    currentCarHotSpotIndexSet: (state: CarSliceType, { payload }) => {
      let index = state.carHotSpotPositions.findIndex(
        (car) => car?.id === state.currentCarHotSpotIndex,
      );
      if (payload === 'increase') {
        index++;
        if (index >= 0 && index < state.carHotSpotPositions?.length) {
          const id = state.carHotSpotPositions[index]?.id;
          state.currentCarHotSpotIndex = id;
        } else {
          const id = state.carHotSpotPositions[0]?.id;
          state.currentCarHotSpotIndex = id;
        }
      } else if (payload === 'decrease') {
        index--;
        if (index >= 0 && index < state.carHotSpotPositions?.length) {
          const id = state.carHotSpotPositions[index]?.id;
          state.currentCarHotSpotIndex = id;
        } else {
          const id =
            state.carHotSpotPositions[state.carHotSpotPositions?.length - 1]
              ?.id;
          state.currentCarHotSpotIndex = id;
        }
      } else state.currentCarHotSpotIndex = payload;
    },

    exterior360ImageSet: (state, { payload }) => {
      state.exterior360Image = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchCarModel.fulfilled,
      (state: CarSliceType, action: PayloadAction<ModelResponse>) => {
        if (!action.payload) {
          state.fetchCarModelStatus = 'rejected';
          return;
        }
        if ('error' in action.payload) {
          const { error } = action.payload as ErrorResponse;
          state.error = error;
          state.fetchCarModelStatus = 'rejected';
          return;
        }

        state.fetchCarModelStatus = 'fulfilled';

        const {
          modelMainSpin,
          interiorModel,
          exteriorModel,
          userId,
          modelId,
          engineVideoFilename,
          autoHotspots,
        } = action.payload as ModelResponseSuccess;

        if (!modelMainSpin) {
          state.error = 'Data not found!';
          state.fetchCarModelStatus = 'rejected';
          return;
        }

        // set engineVideoFilename
        state.autoHotspots = autoHotspots;

        // set engineVideoFilename
        state.engineVideoFilename = engineVideoFilename;

        // set uid
        state.userId = userId;

        // set mid
        state.modelId = modelId;

        // set model main spin
        state.carModel = modelMainSpin;

        // set exterior turntable
        state.exterior360Image = exteriorModel;

        // set interior images
        // state.interiorImages = interiorModel;
        state.centerConsoleUrl = interiorModel?.centerConsoleUrl;
        state.backSeatUrl = interiorModel?.backSeatUrl;

        state.loading = false;
        state.error = null;
      },
    );
    builder.addCase(fetchCarModel.rejected, (state: any) => {
      state.fetchCarModelStatus = 'rejected';
      state.loading = false;
      console.log('@getModel error');
      state.error = 'no record found !';
    });
    builder.addCase(fetchCarModel.pending, (state: CarSliceType) => {
      state.fetchCarModelStatus = 'pending';
      state.loading = true;
    });
  },
});

export const getIsZoomEnabled = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.isZoomEnabled,
);

// exterior
export const getModelId = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.modelId,
);

export const getUserId = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.userId,
);

export const getEngineVideoFilename = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.engineVideoFilename,
);

export const getEngineVideoUrl = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.engineVideoUrl,
);

export const getPushStartVideoUrl = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.pushStartVideoUrl,
);

export const getCarHotSpotEnable = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.carHotSpotEnable,
);

export const getCar360Loaded = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.car360Loaded,
);

export const getdragToExploreVisibility = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.dragToExploreVisibility,
);

export const getRotate360Visibility = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.rotate360Visibility,
);

export const getExterior360ImageSet = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.exterior360Image,
);

export const getCarHotSpotCardOpen = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.carHotSpotCardOpen,
);

export const getCarHotSpotPositions = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.carHotSpotPositions,
);

export const getVideoTourExists = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.videoTourExists,
);

export const getCurrentCarHotSpotIndex = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.currentCarHotSpotIndex,
);

export const getExteriorZoomScale = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.exteriorZoomScale,
);

export const getCarImageRatio = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.carImageRatio,
);

export const getFetchCarModelStatus = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.fetchCarModelStatus,
);
// exterior

export const getCarStatus = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.carStatus,
);

export const getIsFullscreen = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.isFullscreen,
);

export const getLoading = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.loading,
);

export const getInteriorStatus = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.interiorStatus,
);

export const getShowVideo = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.showVideo,
);

export const getActiveTab = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.activeTab,
);

export const getPrevTab = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.prevTab,
);

export const getActiveVideo = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.activeVideo,
);

export const getCenterConsoleUrl = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.centerConsoleUrl,
);

export const getBackSeatUrl = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.backSeatUrl,
);

export const getError = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.error,
);

export const getAutoHotspots = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.autoHotspots,
);

export const getPurchaseDialog = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.purchaseDialog,
);

export const getGalleryTab = createSelector(
  (state: { car: CarSliceType }) => state.car,
  (car) => car.galleryTab,
);

export default carSlice.reducer;

export const {
  showVideoToggled,
  interiorStatusSet,
  isFullScreenToggled,
  carStatusSet,
  activeTabSet,
  activeVideoSet,
  engineVideoUrlSet,
  pushStartVideoUrlSet,
  carImageRatioSet,
  zoomEnabledSet,
  // exterior
  carHotSpotEnableSet,
  closeHotSpotPositionSet,
  car360LoadedSet,
  dragToExploreVisibilitySet,
  rotate360VisibilitySet,
  videoTourExistsSet,
  carHotSpotCardOpenToggled,
  carHotSpotPositionsSet,
  currentCarHotSpotIndexSet,
  exterior360ImageSet,
  exteriorZoomScaleSet,
  purchaseDialogToggled,
  galleryTabSet,
} = carSlice.actions;
